/**
 * JAISIU-3327 / UX-R2 — bulk-select state + handlers for the chat-history
 * sidebar.
 *
 * The sidebar view (`fleet-chat-history-sidebar.ts`) already supports a
 * select-mode UI (checkboxes, top toolbar, "Delete selected" button, etc.)
 * — those props are all optional so the sidebar renders identically when
 * the parent hasn't wired them. The previous UX-R2 fix shipped the view
 * in isolation; this controller is the matching wiring that actually
 * drives the UI from `OpenClawApp`.
 *
 * Contract (mirrors the props on `FleetChatHistorySidebarProps`):
 *   - `selectMode`        → whether the sidebar is in select mode at all
 *   - `selectedKeys`      → keys currently checked
 *   - `allSelectableKeys` → flattened list of every visible key, used to
 *                            drive the "Select all (N)" / "All selected"
 *                            flip in the toolbar
 *
 * The parent is expected to render
 * `applyBulkSelectHandlers(this)` to install the methods on its state, then
 * pass them down to the sidebar view.
 *
 * Bulk-delete reuses `requestSessionDeleteAll` /
 * `confirmSessionDeleteAll` from `./sessions.ts` so the confirm modal and
 * RPC semantics stay consistent with the single-row delete path.
 */

import type { ChatHistoryEntry, ChatHistoryGroup } from "../chat/chat-history-catalog.ts";
import type { GatewaySessionRow } from "../types.ts";
import { confirmSessionDeleteAll, requestSessionDeleteAll, type SessionsState } from "./sessions.ts";

export type BulkSelectState = {
  /** Toggle select mode on / off. Resets the selection when turning off. */
  chatHistorySelectMode: boolean;
  /** Currently checked keys (sidebar-local). */
  chatHistorySelectedKeys: string[];
  /** Cached `GatewaySessionRow[]` snapshot for the bulk-delete confirm. */
  chatHistoryBulkDeleteRows: GatewaySessionRow[];
  /** Row lookup map so the bulk-delete path can resolve keys to rows. */
  chatHistoryBulkDeleteRowByKey?: ReadonlyMap<string, GatewaySessionRow>;
};

export type BulkSelectClient = {
  client: { request: (method: string, params: unknown) => Promise<unknown> } | null;
  connected: boolean;
  /**
   * JAISIU-types-sweep: optional on the type. The runtime fields
   * (`sessionsLoading`, `sessionsError`, `lastError`) feed the
   * `applyBulkSelectHandlers` confirm modal but every test and
   * most external callers construct the BulkSelectClient with just
   * `{client, connected}`. Optional here keeps the type honest
   * about the actual contract — defaults are coalesced inside the
   * helper to `false` / `null` so callers can supply either shape.
   */
  sessionsLoading?: boolean;
  sessionsError?: string | null;
  lastError?: string | null;
};

/**
 * Compute every key visible in the chat-history sidebar (groups + archived).
 * Pure, exported for tests.
 */
export function resolveAllSelectableKeys(
  groups: readonly ChatHistoryGroup[],
  archived: readonly ChatHistoryEntry[],
): string[] {
  const out: string[] = [];
  for (const g of groups) {
    for (const e of g.entries) {
      out.push(e.key);
    }
  }
  for (const e of archived) {
    out.push(e.key);
  }
  return out;
}

/**
 * `selectMode` toggling resets the selection. Operators who exit select
 * mode and re-enter should NOT see their previous checkmarks re-appear —
 * that was the previous behaviour and felt sticky. Reset on exit only.
 */
export function applyToggleSelectMode(state: BulkSelectState): void {
  state.chatHistorySelectMode = !state.chatHistorySelectMode;
  if (!state.chatHistorySelectMode) {
    state.chatHistorySelectedKeys = [];
  }
}

/** Add / remove a single key from the selection set. */
export function applyToggleKeySelected(state: BulkSelectState, key: string): void {
  const trimmed = key.trim();
  if (!trimmed) {
    return;
  }
  const current = Array.isArray(state.chatHistorySelectedKeys) ? state.chatHistorySelectedKeys : [];
  if (current.includes(trimmed)) {
    state.chatHistorySelectedKeys = current.filter((k) => k !== trimmed);
    return;
  }
  state.chatHistorySelectedKeys = [...current, trimmed];
}

/**
 * Replace the selection with every visible key. The list of visible keys
 * is the parent's job — pass it in so this helper stays pure.
 */
export function applySelectAllKeys(state: BulkSelectState, allKeys: readonly string[]): void {
  state.chatHistorySelectedKeys = [...allKeys];
}

export function applyClearSelection(state: BulkSelectState): void {
  state.chatHistorySelectedKeys = [];
}

/**
 * Open the bulk-delete confirm modal. Re-uses the existing
 * `requestSessionDeleteAll` so the UI / RPC behaviour matches the
 * one-shot delete path.
 */
export function requestBulkDelete(
  state: BulkSelectState & BulkSelectClient,
  keys: readonly string[],
  rows: readonly GatewaySessionRow[],
): void {
  const cleaned = keys.map((k) => k.trim()).filter((k) => k.length > 0);
  if (!cleaned.length) {
    return;
  }
  requestSessionDeleteAll(state, cleaned, [...rows]);
}

/**
 * Confirm the pending bulk-delete. On success the caller is responsible
 * for clearing the selection / exiting select mode + refreshing the
 * sidebar. Returns the keys that were actually deleted so the caller
 * can decide what UI side-effects to run (e.g. switch the active chat
 * if the operator deleted the one they had open).
 */
export async function confirmBulkDelete(
  // JAISIU-types-sweep: `confirmSessionDeleteAll` ends with a
  // `loadSessions(state, …)` refresh, which needs the full sessions
  // host — not just the bulk-select slice. Stating that here is
  // behaviour-neutral (`OpenClawApp`, the only production caller,
  // already carries every `SessionsState` field) and stops the
  // compiler from pretending this wrapper can run on a partial host.
  state: BulkSelectState & BulkSelectClient & SessionsState,
): Promise<readonly string[]> {
  return confirmSessionDeleteAll(state);
}

/**
 * Bulk-archive every key. Mirrors the shape of `requestBulkDelete`:
 *   - Pure: no side effects on local state.
 *   - Caller passes its own `keys` (matches `selectedKeys` on the sheet)
 *     so the controller stays a thin dispatcher.
 *   - Caller refreshes the chat catalog after this returns.
 *
 * Filtering keys via `canArchiveSessionKey` keeps the current behaviour
 * of the single-row archive path (JAISIU-3230): the global main session
 * is never archivable.
 */
export function buildBulkArchiveKeys(keys: readonly string[]): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const k of keys) {
    const trimmed = k.trim();
    if (!trimmed || seen.has(trimmed)) continue;
    seen.add(trimmed);
    out.push(trimmed);
  }
  return out;
}
